import { useEffect, useRef, useState } from 'react';
import { Pin, AlertTriangle } from 'lucide-react';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';

/**
 * Powrót z OAuth Pinteresta.
 *
 * Wymianę kodu robi funkcja brzegowa - tutaj lecą tylko `code` i identyfikator
 * użytkownika, token Pinteresta nigdy nie trafia do przeglądarki.
 *
 * Ostrzeżenia z podłączenia (konto nie jest biznesowe, aplikacja jest na Trial
 * access, Pinterest nie przyznał wszystkich zakresów) zatrzymują automatyczne
 * przekierowanie. Toast, który znika po trzech sekundach, to za mało dla
 * informacji "twoje piny będą niewidoczne".
 */
export function PinterestCallback() {
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('Łączenie konta Pinterest...');
  const [warnings, setWarnings] = useState<string[]>([]);

  // React w trybie Strict montuje efekt dwa razy, a kod autoryzacyjny jest jednorazowy.
  const calledRef = useRef(false);

  useEffect(() => {
    if (calledRef.current) return;
    calledRef.current = true;

    const handleCallback = async () => {
      try {
        const params = new URLSearchParams(window.location.search);
        const code = params.get('code');
        const state = params.get('state');
        const error = params.get('error');

        if (error) {
          throw new Error(
            `Pinterest odrzucił autoryzację: ${params.get('error_description') || error}`
          );
        }
        if (!code) throw new Error('Pinterest nie zwrócił kodu autoryzacyjnego');

        const expectedState = localStorage.getItem('pinterest_oauth_state');
        localStorage.removeItem('pinterest_oauth_state');
        if (expectedState && state !== expectedState) {
          throw new Error('Niezgodny parametr state - autoryzacja przerwana ze względów bezpieczeństwa');
        }

        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('Musisz być zalogowany w panelu');

        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
        const redirectUri = `${supabaseUrl}/functions/v1/pinterest-oauth`;

        const response = await fetch(`${supabaseUrl}/functions/v1/pinterest-oauth`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({ code, redirectUri, userId: user.id }),
        });

        const data = await response.json();
        if (!response.ok) {
          throw new Error(data.error || 'Nie udało się połączyć konta Pinterest');
        }

        setStatus('success');
        setMessage(`Podłączono konto ${data.account_name}`);
        setWarnings(Array.isArray(data.warnings) ? data.warnings : []);
        toast.success(`Podłączono ${data.account_name}`);

        // Z ostrzeżeniami zostajemy na ekranie - użytkownik wraca sam.
        if (!data.warnings || data.warnings.length === 0) {
          const redirectUrl = localStorage.getItem('social_auth_redirect') || '/social/accounts';
          localStorage.removeItem('social_auth_redirect');
          setTimeout(() => { window.location.href = redirectUrl; }, 2000);
        }
      } catch (err) {
        console.error('Pinterest callback error:', err);
        setStatus('error');
        setMessage(err instanceof Error ? err.message : 'Wystąpił błąd');
        toast.error(err instanceof Error ? err.message : 'Nie udało się połączyć konta Pinterest');
      }
    };

    handleCallback();
  }, []);

  const goBack = () => {
    const redirectUrl = localStorage.getItem('social_auth_redirect') || '/social/accounts';
    localStorage.removeItem('social_auth_redirect');
    window.location.href = redirectUrl;
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <Pin className="h-12 w-12 text-[#E60023]" />
        </div>
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          {status === 'loading' && 'Łączenie z Pinterestem...'}
          {status === 'success' && 'Konto podłączone'}
          {status === 'error' && 'Połączenie nieudane'}
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">{message}</p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10 space-y-4">
          {status === 'loading' && (
            <div className="flex justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E60023]" />
            </div>
          )}

          {status === 'error' && (
            <div className="rounded-md bg-red-50 p-4 text-sm font-medium text-red-800">{message}</div>
          )}

          {status === 'success' && warnings.length === 0 && (
            <div className="rounded-md bg-green-50 p-4 text-sm font-medium text-green-800">
              Konto jest gotowe do publikacji. Za chwilę wrócisz do panelu.
            </div>
          )}

          {warnings.length > 0 && (
            <div className="rounded-md bg-amber-50 border border-amber-200 p-4 space-y-2">
              <div className="flex items-center gap-2 text-amber-900 font-medium text-sm">
                <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                <span>Przeczytaj zanim zaczniesz publikować</span>
              </div>
              <ul className="list-disc list-inside space-y-2 text-sm text-amber-900">
                {warnings.map((warning, index) => <li key={index}>{warning}</li>)}
              </ul>
            </div>
          )}

          {status !== 'loading' && (
            <button
              onClick={goBack}
              className="w-full px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800"
            >
              Wróć do panelu
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
